/* ═══════════════════════════════════════════════════════════════
   RATE CARD — VawWorld Budget Estimator
   ───────────────────────────────────────────────────────────────
   ALL prices, complexity multipliers and baseline factors live here.
   Owner can edit any number below without touching component logic.
   Values marked "// PLACEHOLDER" are draft rates — replace with the
   studio's real per-unit numbers before going live.

   Base currency: USD. INR / EUR are derived at render time using the
   FX_RATES table below (also editable).
   ═══════════════════════════════════════════════════════════════ */

export const CURRENCIES = ["USD", "INR", "EUR"];

/* FX from 1 USD → target. Edit as needed. */
export const FX_RATES = {
  USD: 1,
  INR: 83,     // PLACEHOLDER — update to live rate periodically
  EUR: 0.92,   // PLACEHOLDER — update to live rate periodically
};

export const CURRENCY_SYMBOLS = {
  USD: "$",
  INR: "₹",
  EUR: "€",
};

/* ─── Complexity / difficulty multipliers ─────────────────────── */
export const COMPLEXITY_TIERS = {
  Standard: 1.0,
  Detailed: 1.35,
  Premium: 1.80,
};

export const DIFFICULTY_TIERS = {
  Easy: 1.0,
  Medium: 1.5,
  Hard: 2.2,
};

export const DETAIL_TIERS = {
  Rough: 1.0,
  Polished: 1.65,
};

export const STYLE_TIERS = {
  Flat: 1.0,
  Broadcast: 1.4,
  Premium: 1.9,
};

/* ─── PROJECT-TYPE factor for 3D CGI (feature vs series etc.) ── */
export const CGI_PROJECT_FACTOR = {
  Feature: 1.0,
  Series: 0.85,
  Short: 1.1,
  Commercial: 1.2,
};

/* ─── Baseline (market / in-house) multiplier ─────────────────── */
/* This is what the SAME scope costs at a mid-tier US / EU studio.
   Savings % = 1 − (Vawworld total / baseline total). */
export const BASELINE_MULTIPLIER = 2.35;   // PLACEHOLDER — replace with Vawworld benchmark

/* ─── Government / policy discounts applied to the subtotal ────
   These stack ADDITIVELY (25% + 15% = 40% off the raw subtotal).
   Grand total = subtotal × (1 − combined). Owner: edit values here. */
export const DISCOUNTS = [
  { id: "avgc", label: "AVGC Discount", pct: 0.25 }, // PLACEHOLDER — India AVGC policy incentive
  { id: "t3",   label: "T3 Discount",   pct: 0.15 }, // PLACEHOLDER — Tier-3 studio incentive
];

/* ═══════════════════════════════════════════════════════════════
   PER-CATEGORY LINE ITEMS
   Each item declares:
     unit           — how quantity is measured (used in the top sheet)
     baseRate       — USD per unit at Standard/Medium tier
     modifiers      — which tier table applies (optional)
   ═══════════════════════════════════════════════════════════════ */

export const RATE_CARD = {
  cgi3d: {
    label: "3D CGI Animation",
    lineItems: {
      runtime:      { unit: "minute",    baseRate: 15000, mod: "COMPLEXITY" }, // Vawworld — per finished minute
      hero:         { unit: "character", baseRate: 4000,  mod: "COMPLEXITY" }, // Vawworld — hero character
      secondary:    { unit: "character", baseRate: 1500,  mod: "COMPLEXITY" }, // Vawworld — secondary character
      prop:         { unit: "prop",      baseRate: 350,   mod: "COMPLEXITY" }, // Vawworld — hero prop
      environment:  { unit: "env",       baseRate: 6800,  mod: "COMPLEXITY" }, // Vawworld — set/environment
      soundDesign:  { unit: "minute",    baseRate: 400,   mod: "COMPLEXITY" }, // Vawworld — sound design per finished minute
    },
  },
  vfx: {
    label: "VFX (Live-Action)",
    lineItems: {
      wireRemoval:    { unit: "shot", baseRate: 380, mod: "DIFFICULTY" }, // PLACEHOLDER
      rotoscoping:    { unit: "shot", baseRate: 520, mod: "DIFFICULTY" }, // PLACEHOLDER
      cleanupPaint:   { unit: "shot", baseRate: 460, mod: "DIFFICULTY" }, // PLACEHOLDER
      mattePainting:  { unit: "shot", baseRate: 1650, mod: "DIFFICULTY" }, // PLACEHOLDER
      cgIntegration:  { unit: "shot", baseRate: 2200, mod: "DIFFICULTY" }, // PLACEHOLDER
      compositing:    { unit: "shot", baseRate: 640, mod: "DIFFICULTY" }, // PLACEHOLDER
    },
    /* extra per-second factor applied on top of shot rate */
    shotLengthFactor: 0.06, // PLACEHOLDER — add 6% per avg-second above 4s baseline
  },
  animatic: {
    label: "Animatics / Previz",
    lineItems: {
      runtime: { unit: "minute", baseRate: 2200, mod: "DETAIL" }, // PLACEHOLDER
      panels:  { unit: "panel",  baseRate: 55,   mod: "DETAIL" }, // PLACEHOLDER
      scene:   { unit: "scene",  baseRate: 340,  mod: "DETAIL" }, // PLACEHOLDER
    },
  },
  anim2d: {
    label: "2D Animation",
    lineItems: {
      runtime:    { unit: "minute",    baseRate: 18000, mod: "STYLE" }, // PLACEHOLDER
      character:  { unit: "character", baseRate: 2400,  mod: "STYLE" }, // PLACEHOLDER
      background: { unit: "background", baseRate: 480, mod: "STYLE" }, // PLACEHOLDER
    },
  },
  mocap: {
    label: "Mocap Cleanup",
    lineItems: {
      takes:   { unit: "take",   baseRate: 260, mod: "DIFFICULTY" }, // PLACEHOLDER
      seconds: { unit: "second", baseRate: 12,  mod: "DIFFICULTY" }, // PLACEHOLDER
    },
  },
};

/* Helper — look up the correct modifier table by key */
export const MODIFIER_TABLES = {
  COMPLEXITY: COMPLEXITY_TIERS,
  DIFFICULTY: DIFFICULTY_TIERS,
  DETAIL: DETAIL_TIERS,
  STYLE: STYLE_TIERS,
};

/* ═══════════════════════════════════════════════════════════════
   NOTIFICATION INBOX — where estimates are emailed on submit
   Owner: edit ONLY the OWNER_EMAIL string.
   ═══════════════════════════════════════════════════════════════ */
export const OWNER_EMAIL = "kumar@vawworldstudios.com";

/* ═══════════════════════════════════════════════════════════════
   QUALITY BRACKETS — public preview reels shown to the client
   in place of the internal line-item breakdown.
   Edit videoId / description freely — nothing else needs to change.
   ═══════════════════════════════════════════════════════════════ */
export const QUALITY_TIERS = [
  {
    id: "tier-1",
    label: "$400K – $750K",
    minUsd: 0,
    maxUsd: 750000,
    videoId: "zz_CEwqZYn4",
    headline: "Broadcast-grade CGI at a fraction of Western studio cost",
    description:
      "This is what $6M looks like anywhere else — delivered end-to-end from our Mumbai and Bhopal floors with the same shading, lighting and animation stack the majors use. Same craft, unmatchable value.",
  },
  {
    id: "tier-2",
    label: "$750K – $1.5M",
    minUsd: 750000,
    maxUsd: 1500000,
    videoId: "FfubD6zbSsk",
    headline: "Feature-film craft, engineered to a lean budget",
    description:
      "This bracket buys full theatrical polish — cinematic lighting, hero-grade character animation and print-master sound — for what a single act would cost in Culver City. Cannes-ready quality without the Cannes-ready invoice.",
  },
  {
    id: "tier-3",
    label: "$1.5M – $2M+",
    minUsd: 1500000,
    maxUsd: Infinity,
    videoId: "ETlOSBR92Fs",
    headline: "The ceiling of what an independent studio can produce — anywhere",
    description:
      "IMAX-scale visuals, Dolby-mixed audio and pipeline-hardened delivery. This is the top-of-market Vawworld shot: unmatchable in the Global South, and still 60%+ under equivalent US spend. Screen-tested at global markets from Cannes to MIPCOM.",
  },
];

export function tierForBudget(usdAmount) {
  const amt = Number(usdAmount) || 0;
  return (
    QUALITY_TIERS.find((t) => amt >= t.minUsd && amt < t.maxUsd) ||
    QUALITY_TIERS[QUALITY_TIERS.length - 1]
  );
}
